// v10/src/components/admin/fuel/AdjustmentModal.js
//
// Modal "Ajuste manual de stock" — registra movement_type='adjustment'.
// `direction` add/subtract es obligatorio. `reason` también es obligatorio
// (≥5 chars) porque queda registrado para auditoría.

import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Loader, SlidersHorizontal, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import fuelService from '../../../services/fuelService';

const formatNum = (n, locale = 'es-AR') => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString(locale, { maximumFractionDigits: 2 });
};

const AdjustmentModal = ({ tanks = [], preselectedTankId, onSuccess, onClose, darkMode, locale = 'es-AR' }) => {
  const { t } = useTranslation('admin');

  const activeTanks = useMemo(
    () => tanks.filter((tk) => tk.is_active !== false),
    [tanks]
  );

  const initialTankId = preselectedTankId
    || (activeTanks.length === 1 ? activeTanks[0].id : '');

  const [form, setForm] = useState({
    fuel_tank_id:    initialTankId,
    direction:       'add',
    quantity_liters: '',
    reason:          '',
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !loading) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, loading]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const selectedTank = useMemo(
    () => activeTanks.find((tk) => tk.id === form.fuel_tank_id),
    [activeTanks, form.fuel_tank_id]
  );

  const stockPreview = useMemo(() => {
    if (!selectedTank) return null;
    const qty = Number(form.quantity_liters);
    if (!Number.isFinite(qty) || qty <= 0) return null;
    const current  = Number(selectedTank.current_stock_liters) || 0;
    const capacity = Number(selectedTank.capacity_liters)      || 0;
    const after = form.direction === 'add' ? current + qty : current - qty;
    const violatesNeg = after < 0;
    const violatesCap = after > capacity;
    return { after, violatesNeg, violatesCap, capacity };
  }, [selectedTank, form.quantity_liters, form.direction]);

  const validation = useMemo(() => {
    const errors = [];
    if (!form.fuel_tank_id) errors.push(t('fuel_adjustment_modal_err_tank_required'));
    const qty = Number(form.quantity_liters);
    if (!Number.isFinite(qty) || qty <= 0) errors.push(t('fuel_adjustment_modal_err_qty_positive'));
    if (form.reason.trim().length < 5) errors.push(t('fuel_adjustment_modal_err_reason_required'));

    if (stockPreview?.violatesNeg) {
      errors.push(t('fuel_adjustment_modal_err_negative_stock'));
    }
    if (stockPreview?.violatesCap) {
      errors.push(t('fuel_adjustment_modal_err_exceeds_capacity', {
        capacity: formatNum(stockPreview.capacity, locale),
      }));
    }
    return { errors, ok: errors.length === 0 };
  }, [form, stockPreview, t, locale]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validation.ok || loading) return;
    setLoading(true); setError('');
    try {
      await fuelService.createMovement({
        fuel_tank_id:    form.fuel_tank_id,
        movement_type:   'adjustment',
        direction:       form.direction,
        quantity_liters: Number(form.quantity_liters),
        reason:          form.reason.trim(),
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || t('fuel_validation_network_error'));
      setLoading(false);
    }
  };

  // Estilos compartidos
  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.45)' : '#64748b';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.30)' : '#94a3b8';
  const labelStyle = {
    margin: '0 0 6px', fontSize: 12, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.06em', color: fgFaint,
  };
  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 12,
    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0',
    background: darkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
    color: fgPrimary, fontSize: 14, fontFamily: 'inherit',
    outline: 'none', boxSizing: 'border-box',
  };

  const previewColor = stockPreview && (stockPreview.violatesNeg || stockPreview.violatesCap)
    ? '#ef4444'
    : '#16a34a';

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
      }}
      onClick={loading ? undefined : onClose}
    >
      <motion.div
        initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }}
        style={{
          width: '100%', maxWidth: 560, borderRadius: 24,
          background: darkMode ? 'rgba(15,23,42,0.98)' : 'white',
          border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
          padding: 28, boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
          maxHeight: '90vh', overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 40, height: 40, borderRadius: 12,
                background: 'linear-gradient(135deg,#ea580c,#f97316)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 6px 16px rgba(234,88,12,0.32)',
              }}
            >
              <SlidersHorizontal style={{ width: 20, height: 20, color: 'white' }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: fgPrimary }}>
                {t('fuel_adjustment_modal_title')}
              </h3>
              <p style={{ margin: '3px 0 0', fontSize: 13, color: fgMuted }}>
                {t('fuel_adjustment_modal_subtitle')}
              </p>
            </div>
          </div>
          <button
            type="button" onClick={onClose} disabled={loading}
            aria-label="close"
            style={{
              padding: 8, borderRadius: 10, border: 'none',
              background: 'transparent', cursor: loading ? 'not-allowed' : 'pointer',
              color: fgMuted,
            }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {/* Banner audit */}
        <div
          style={{
            padding: '10px 14px', borderRadius: 12,
            background: 'rgba(234,88,12,0.08)',
            border: '1px solid rgba(234,88,12,0.25)',
            display: 'flex', alignItems: 'flex-start', gap: 10,
            marginBottom: 16,
          }}
        >
          <AlertTriangle style={{ width: 15, height: 15, color: '#ea580c', flexShrink: 0, marginTop: 1 }} />
          <p style={{ margin: 0, fontSize: 12, color: '#ea580c', fontWeight: 600, lineHeight: 1.5 }}>
            {t('fuel_adjustment_modal_audit_warning')}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Tanque */}
          <div style={{ marginBottom: 14 }}>
            <p style={labelStyle}>{t('fuel_adjustment_modal_label_tank')}</p>
            <select
              value={form.fuel_tank_id}
              onChange={(e) => setField('fuel_tank_id', e.target.value)}
              style={{ ...inputStyle, cursor: 'pointer', appearance: 'auto' }}
            >
              <option value="">{t('fuel_adjustment_modal_placeholder_tank')}</option>
              {activeTanks.map((tk) => (
                <option key={tk.id} value={tk.id}>
                  {tk.name} · {tk.fuel_type} · {formatNum(tk.current_stock_liters, locale)}L / {formatNum(tk.capacity_liters, locale)}L
                </option>
              ))}
            </select>
          </div>

          {/* Direction */}
          <div style={{ marginBottom: 14 }}>
            <p style={labelStyle}>{t('fuel_adjustment_modal_label_direction')}</p>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { key: 'add',      label: t('fuel_adjustment_modal_direction_add'),      color: '#16a34a' },
                { key: 'subtract', label: t('fuel_adjustment_modal_direction_subtract'), color: '#dc2626' },
              ].map((opt) => {
                const selected = form.direction === opt.key;
                return (
                  <motion.button
                    key={opt.key}
                    type="button"
                    onClick={() => setField('direction', opt.key)}
                    whileTap={{ scale: 0.96 }}
                    style={{
                      flex: 1, padding: '12px 10px', borderRadius: 12,
                      border: `2px solid ${selected ? opt.color : (darkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0')}`,
                      background: selected ? `${opt.color}15` : 'transparent',
                      cursor: 'pointer', fontFamily: 'inherit',
                      fontSize: 13, fontWeight: 800,
                      color: selected ? opt.color : (darkMode ? 'rgba(255,255,255,0.45)' : '#94a3b8'),
                      transition: 'all 0.15s',
                    }}
                  >
                    {opt.label}
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Cantidad */}
          <div style={{ marginBottom: 14 }}>
            <p style={labelStyle}>{t('fuel_adjustment_modal_label_qty')}</p>
            <input
              type="number" min="0" step="0.01" style={inputStyle}
              value={form.quantity_liters}
              onChange={(e) => setField('quantity_liters', e.target.value)}
              placeholder="50"
            />
            {stockPreview && (
              <p style={{ margin: '6px 0 0', fontSize: 12, color: previewColor, fontWeight: 700 }}>
                {t('fuel_adjustment_modal_preview', { stock: formatNum(stockPreview.after, locale) })}
              </p>
            )}
          </div>

          {/* Reason — obligatorio */}
          <div style={{ marginBottom: 16 }}>
            <p style={labelStyle}>{t('fuel_adjustment_modal_label_reason')}</p>
            <textarea
              rows={3} style={{ ...inputStyle, resize: 'vertical', minHeight: 70 }}
              value={form.reason}
              onChange={(e) => setField('reason', e.target.value)}
              placeholder={t('fuel_adjustment_modal_placeholder_reason')}
            />
            <p style={{ margin: '6px 0 0', fontSize: 11, color: fgFaint, lineHeight: 1.4 }}>
              {t('fuel_adjustment_modal_hint_reason')}
            </p>
          </div>

          {validation.errors.length > 0 && (
            <div
              style={{
                padding: '10px 14px', borderRadius: 12,
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.2)',
                marginBottom: 12,
              }}
            >
              {validation.errors.map((e, i) => (
                <p key={i} style={{ margin: i === 0 ? 0 : '4px 0 0', fontSize: 12, color: '#ef4444', fontWeight: 600 }}>
                  • {e}
                </p>
              ))}
            </div>
          )}

          {error && (
            <div
              style={{
                padding: '10px 14px', borderRadius: 12,
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.2)',
                marginBottom: 12,
              }}
            >
              <p style={{ margin: 0, fontSize: 13, color: '#ef4444', fontWeight: 600 }}>{error}</p>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <motion.button
              type="submit" disabled={loading || !validation.ok}
              whileHover={!loading && validation.ok ? { scale: 1.02 } : {}}
              whileTap={!loading && validation.ok ? { scale: 0.97 } : {}}
              style={{
                flex: 1, padding: 13, borderRadius: 14, border: 'none',
                cursor: loading || !validation.ok ? 'not-allowed' : 'pointer',
                background: loading || !validation.ok
                  ? (darkMode ? 'rgba(255,255,255,0.08)' : '#cbd5e1')
                  : 'linear-gradient(135deg,#ea580c,#f97316)',
                color: 'white', fontWeight: 700, fontSize: 14, fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
              {loading
                ? <Loader style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
                : <SlidersHorizontal style={{ width: 16, height: 16 }} />
              }
              {loading ? t('fuel_adjustment_modal_btn_saving') : t('fuel_adjustment_modal_btn_submit')}
            </motion.button>
            <button
              type="button" onClick={onClose} disabled={loading}
              style={{
                padding: '13px 18px', borderRadius: 14,
                border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                background: 'transparent',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: 600, fontSize: 14, color: fgMuted,
                fontFamily: 'inherit',
              }}
            >
              {t('fuel_modal_btn_cancel')}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default AdjustmentModal;
